import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { useStockTakers, useRecordPurchase, useRecordTakeOut, useRecordRecount } from '../../hooks/useStock';
import { newIdempotencyKey } from '../../services/stockApi';
import { formatCount, formatSignedCount, parseWholeNumber } from '../../utils/numberFormatter';
import { TEXT, UNIT_LABELS, ENTRY_ACTIONS, CREW_ROLES, errorText, itemNames } from '../../config/stockLabels';

/**
 * The one form behind «استلام مشتريات», «تسليم للفريق» and «جرد المخزن» (tasks/stock.md D6, D8).
 * Every active item is listed; ops fill in only the ones that moved, and one submit records them all.
 *
 * ── Exactly once ─────────────────────────────────────────────────────────────────────────────────
 * One key per OPENING of the form (D11), kept across retries: a double tap or a retry after a lost reply
 * is recorded once. Closing and reopening starts a new submit with a new key.
 *
 * ── The count is blind ───────────────────────────────────────────────────────────────────────────
 * The recount form does not show what the history expects — a counter who can see the expected
 * figure tends to find it. The difference is shown after saving, from the server's answer.
 *
 * Full screen on a phone: the count is taken standing at the shelf.
 */
const StockEntrySheet = ({ kind, show, onHide, storeId, items }) => {
  const action = ENTRY_ACTIONS[kind] || ENTRY_ACTIONS.purchase;
  const isCount = kind === 'recount';
  const isTakeOut = kind === 'takeOut';

  // All three are called every render (rules of hooks); the form uses the one for its kind.
  const mutations = { purchase: useRecordPurchase(), takeOut: useRecordTakeOut(), recount: useRecordRecount() };
  const mutation = mutations[kind] || mutations.purchase;
  const { data: takers = [], isLoading: takersLoading } = useStockTakers(show && isTakeOut);

  const [values, setValues] = useState({});
  const [takenBy, setTakenBy] = useState('');
  const [note, setNote] = useState('');
  const [submitKey, setSubmitKey] = useState('');
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // Every opening is a new submit: clean fields, a new key (the StopUnitModal rule).
  useEffect(() => {
    if (!show) return;
    setValues({});
    setTakenBy('');
    setNote('');
    setError(null);
    setResult(null);
    setSubmitKey(newIdempotencyKey());
  }, [show, kind]);

  // What has been typed, read in either digit system. `invalid` maps a row that cannot be saved to the
  // sentence that says why: not a whole number, or (purchase / handover) a number below 1.
  const { lines, invalid } = useMemo(() => {
    const out = [];
    const bad = new Map();
    for (const item of items) {
      const raw = values[item.id];
      if (raw === undefined || String(raw).trim() === '') continue;
      const n = parseWholeNumber(raw);
      if (n === null) { bad.set(item.id, TEXT.wholeNumber); continue; }
      if (!isCount && n < 1) { bad.set(item.id, TEXT.atLeastOne); continue; }
      out.push(isCount ? { itemId: item.id, countedQuantity: n } : { itemId: item.id, quantity: n });
    }
    return { lines: out, invalid: bad };
  }, [values, items, isCount]);

  const crew = takers.filter(t => CREW_ROLES.includes(t.role));
  const others = takers.filter(t => !CREW_ROLES.includes(t.role));
  const takerLabel = (t) => `${t.name || TEXT.unnamed}${t.status === 'invited' ? ` — ${TEXT.invited}` : ''}`;

  const canSave = lines.length > 0 && invalid.size === 0 && (!isTakeOut || takenBy) && !mutation.isPending;

  const save = async () => {
    setError(null);
    if (!lines.length) { setError(TEXT.nothingEntered); return; }
    try {
      const res = await mutation.mutateAsync({
        storeId,
        lines,
        note: note.trim() || undefined,
        idempotencyKey: submitKey,
        ...(isTakeOut ? { takenBy } : {})
      });
      setResult(res);
    } catch (err) {
      setError(errorText(err));
    }
  };

  const nameOf = (itemId) => items.find(i => String(i.id) === String(itemId));

  return (
    <Modal show={show} onHide={onHide} centered scrollable fullscreen="sm-down" dir="rtl" dialogClassName="stk-modal">
      <Modal.Header closeButton>
        <Modal.Title className="stk-modal__title">
          <i className={`bi ${action.icon}`} aria-hidden="true"></i> {action.title}
        </Modal.Title>
      </Modal.Header>

      {result ? (
        <>
          <Modal.Body>
            <div className="stk-result" role="status">
              <i className="bi bi-check-circle-fill" aria-hidden="true"></i>
              <p className="stk-result__title">{action.saved}</p>
              {result.replayed > 0 && result.replayed === result.entries.length && (
                <p className="stk-muted">{TEXT.replayedNote}</p>
              )}
            </div>
            {isCount && (
              <>
                <h4 className="stk-subtitle">{TEXT.recountResult}</h4>
                <ul className="stk-diff">
                  {result.entries.map((e) => {
                    const item = nameOf(e.item);
                    return (
                      <li key={e._id} className="stk-diff__row">
                        <span className="stk-diff__name">{item ? itemNames(item).primary : ''}</span>
                        <span className="stk-diff__found">
                          {TEXT.recountFound}: {formatCount(e.countedQuantity)} {item ? UNIT_LABELS[item.unit] : ''}
                        </span>
                        <span className={`stk-diff__delta ${e.quantity === 0 ? 'is-match' : e.quantity > 0 ? 'is-up' : 'is-down'}`}>
                          {e.quantity === 0
                            ? TEXT.recountMatch
                            : <bdi dir="ltr">{e.quantity > 0 ? '+' : '−'}{formatCount(Math.abs(e.quantity))}</bdi>}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={onHide}>{TEXT.done}</Button>
          </Modal.Footer>
        </>
      ) : (
        <>
          <Modal.Body>
            <p className="stk-muted stk-sheet__hint">{action.hint}</p>

            {isTakeOut && (
              <Form.Group className="stk-field" controlId="stk-taker">
                <Form.Label>{TEXT.taker}</Form.Label>
                <Form.Select value={takenBy} onChange={(e) => setTakenBy(e.target.value)} disabled={takersLoading} required>
                  <option value="">{TEXT.takerPlaceholder}</option>
                  {crew.length > 0 && (
                    <optgroup label={TEXT.takerGroupCrew}>
                      {crew.map(t => <option key={t.id} value={t.id}>{takerLabel(t)}</option>)}
                    </optgroup>
                  )}
                  {others.length > 0 && (
                    <optgroup label={TEXT.takerGroupOps}>
                      {others.map(t => <option key={t.id} value={t.id}>{takerLabel(t)}</option>)}
                    </optgroup>
                  )}
                </Form.Select>
              </Form.Group>
            )}

            <ul className="stk-lines">
              {items.map((item) => {
                const { primary, secondary } = itemNames(item);
                const problem = invalid.get(item.id);
                const bad = Boolean(problem);
                const inputId = `stk-line-${item.id}`;
                return (
                  <li key={item.id} className={`stk-line${bad ? ' stk-line--bad' : ''}`}>
                    <span className="stk-line__photo" aria-hidden="true">
                      {item.photoUrl ? <img src={item.photoUrl} alt="" loading="lazy" /> : <i className="bi bi-box-seam"></i>}
                    </span>
                    <label className="stk-line__names" htmlFor={inputId}>
                      <span className="stk-line__name">{primary}</span>
                      <span className="stk-line__name-en" dir="ltr">{secondary}</span>
                      {!isCount && (
                        <span className="stk-line__onhand">
                          {TEXT.onShelf}: <bdi dir="ltr">{formatSignedCount(item.quantity)}</bdi> {UNIT_LABELS[item.unit]}
                        </span>
                      )}
                    </label>
                    <span className="stk-line__qty">
                      <input
                        id={inputId}
                        className="form-control stk-line__input"
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        value={values[item.id] ?? ''}
                        onChange={(e) => setValues(v => ({ ...v, [item.id]: e.target.value }))}
                        aria-invalid={bad || undefined}
                        aria-describedby={bad ? `${inputId}-err` : undefined}
                      />
                      <span className="stk-line__unit">{UNIT_LABELS[item.unit] || item.unit}</span>
                    </span>
                    {bad && (
                      <span id={`${inputId}-err`} className="stk-line__err">{problem}</span>
                    )}
                  </li>
                );
              })}
            </ul>

            <Form.Group className="stk-field" controlId="stk-note">
              <Form.Label>{TEXT.note}</Form.Label>
              <Form.Control as="textarea" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
            </Form.Group>

            {error && <div className="stk-error" role="alert">{error}</div>}
          </Modal.Body>
          <Modal.Footer className="stk-sheet__foot">
            <span className="stk-muted">{lines.length > 0 ? TEXT.linesChosen(lines.length) : ''}</span>
            <div className="stk-sheet__buttons">
              <Button variant="outline-secondary" onClick={onHide} disabled={mutation.isPending}>{TEXT.cancel}</Button>
              <Button variant="primary" onClick={save} disabled={!canSave}>
                {mutation.isPending && <Spinner animation="border" size="sm" className="stk-spin" />}
                {action.save}
              </Button>
            </div>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};

export default StockEntrySheet;
